import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  private buildParams(params?: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key]);
        }
      });
    }
    return httpParams;
  }

  // Auth
  login(email: string, password: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/login`, { email, password });
  }

  getMe(): Observable<any> {
    return this.http.get(`${this.baseUrl}/auth/me`);
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/forgot-password`, { email });
  }

  resetPassword(token: string, password: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/reset-password`, { token, password });
  }

  changePassword(currentPassword: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/change-password`, { currentPassword, newPassword });
  }

  // Generic CRUD
  getList(resource: string, params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/${resource}`, { params: this.buildParams(params) });
  }

  // Alias for getList - generic GET
  get(resource: string, params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/${resource}`, { params: this.buildParams(params) });
  }

  getOne(resource: string, id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/${resource}/${id}`);
  }

  create(resource: string, data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/${resource}`, data);
  }

  // Alias for create - generic POST
  post(resource: string, data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/${resource}`, data);
  }

  update(resource: string, id: number | string, data: Record<string, any>): Observable<any> {
    return this.http.put(`${this.baseUrl}/${resource}/${id}`, data);
  }

  // Alias for update - generic PUT (without id in path)
  put(resource: string, data: Record<string, any>): Observable<any> {
    return this.http.put(`${this.baseUrl}/${resource}`, data);
  }

  remove(resource: string, id: number | string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${resource}/${id}`);
  }

  // Special endpoints
  getCompany360(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/companies/${id}/360`);
  }

  getBackupLeads(lat: number, lng: number, limit = 25): Observable<any> {
    return this.http.get(`${this.baseUrl}/leads/backup`, {
      params: this.buildParams({ lat, lng, limit })
    });
  }

  getAgentDailyPlan(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/agents/${id}/daily-plan`);
  }

  getAgentKpi(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/kpi/agent/${id}`);
  }

  getManagerKpi(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/kpi/manager/${id}`);
  }

  getSystemKpi(): Observable<any> {
    return this.http.get(`${this.baseUrl}/kpi/system`);
  }

  getKpiAlerts(): Observable<any> {
    return this.http.get(`${this.baseUrl}/kpi/alerts`);
  }

  getKpiSnapshots(userId: number | string, period = 'daily'): Observable<any> {
    return this.http.get(`${this.baseUrl}/kpi/snapshots/${userId}`, {
      params: this.buildParams({ period })
    });
  }

  transitionOffer(id: number | string, status: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/offers/${id}/transition`, { status });
  }

  getOfferReplies(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/offers/${id}/replies`);
  }

  createOfferReply(id: number | string, data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/offers/${id}/replies`, data);
  }

  confirmOffer(id: number | string, data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/offers/${id}/confirm`, data);
  }

  // Phase 2 endpoints
  getOptimizedRoute(agentId: number | string, date?: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/agents/${agentId}/optimized-route`, {
      params: date ? this.buildParams({ date }) : undefined
    });
  }

  recalculateLeadScores(): Observable<any> {
    return this.http.post(`${this.baseUrl}/leads/recalculate-scores`, {});
  }

  getFeed(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/feed`, { params: this.buildParams(params) });
  }

  getEscalations(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/escalations`, { params: this.buildParams(params) });
  }

  checkEscalations(): Observable<any> {
    return this.http.post(`${this.baseUrl}/escalations/check`, {});
  }

  resolveEscalation(id: number | string): Observable<any> {
    return this.http.post(`${this.baseUrl}/escalations/${id}/resolve`, {});
  }

  getWinLossAnalysis(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/analytics/win-loss`, { params: this.buildParams(params) });
  }

  getTerritoryHeatmap(cellSize?: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/analytics/territory-heatmap`, {
      params: cellSize ? this.buildParams({ cell_size: cellSize }) : undefined
    });
  }

  getSeasonalPatterns(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/analytics/seasonal-patterns`, { params: this.buildParams(params) });
  }

  getRevenueForecast(months?: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/analytics/forecast`, {
      params: months ? this.buildParams({ months }) : undefined
    });
  }

  getCompetitorAnalytics(): Observable<any> {
    return this.http.get(`${this.baseUrl}/analytics/competitors`);
  }

  getCompetitors(): Observable<any> {
    return this.http.get(`${this.baseUrl}/competitors`);
  }

  createCompetitor(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/competitors`, data);
  }

  addCompetitorMention(id: number | string, data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/competitors/${id}/mentions`, data);
  }

  getCompanyRelationships(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/companies/${id}/relationships`);
  }

  addCompanyRelationship(id: number | string, data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/companies/${id}/relationships`, data);
  }

  getCompanyHealthAlerts(): Observable<any> {
    return this.http.get(`${this.baseUrl}/companies/health-alerts`);
  }

  uploadVoiceNote(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/voice-notes/upload`, data);
  }

  createCheckIn(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/check-ins`, data);
  }

  getTodayCheckIns(): Observable<any> {
    return this.http.get(`${this.baseUrl}/check-ins/today`);
  }

  getTemplates(): Observable<any> {
    return this.http.get(`${this.baseUrl}/templates`);
  }

  createTemplate(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/templates`, data);
  }

  renderTemplate(id: number | string, variables: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/templates/${id}/render`, { variables });
  }

  getMeetingPrep(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/meetings/${id}/prep`);
  }

  autoAssignManagers(): Observable<any> {
    return this.http.post(`${this.baseUrl}/managers/auto-assign`, {});
  }

  getAgentDigest(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/digest/agent/${id}`);
  }

  getManagerDigest(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/digest/manager/${id}`);
  }

  getSystemDigest(): Observable<any> {
    return this.http.get(`${this.baseUrl}/digest/system`);
  }

  getDigest(type: 'agent' | 'manager' | 'system', id?: number | string): Observable<any> {
    if (type === 'agent' && id) return this.http.get(`${this.baseUrl}/digest/agent/${id}`);
    if (type === 'manager' && id) return this.http.get(`${this.baseUrl}/digest/manager/${id}`);
    return this.http.get(`${this.baseUrl}/digest/system`);
  }

  requestApproval(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/approvals/request`, data);
  }

  getPendingApprovals(): Observable<any> {
    return this.http.get(`${this.baseUrl}/approvals/pending`);
  }

  approveRequest(id: number | string): Observable<any> {
    return this.http.post(`${this.baseUrl}/approvals/${id}/approve`, {});
  }

  rejectRequest(id: number | string, notes?: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/approvals/${id}/reject`, { notes });
  }

  getExchangeRates(): Observable<any> {
    return this.http.get(`${this.baseUrl}/exchange-rates`);
  }

  // NIPT + QKB
  lookupNIPT(nipt: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/companies/lookup-nipt/${nipt}`);
  }

  enrichQKB(companyId: number | string): Observable<any> {
    return this.http.post(`${this.baseUrl}/companies/enrich-qkb/${companyId}`, {});
  }

  // WhatsApp
  getWhatsAppTemplates(): Observable<any> {
    return this.http.get(`${this.baseUrl}/whatsapp/templates`);
  }

  createWhatsAppTemplate(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/whatsapp/templates`, data);
  }

  sendWhatsApp(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/whatsapp/send`, data);
  }

  getWhatsAppConversation(contactId: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/whatsapp/conversations/${contactId}`);
  }

  getWhatsAppStats(): Observable<any> {
    return this.http.get(`${this.baseUrl}/whatsapp/stats`);
  }

  // Payments
  recordPayment(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/payments`, data);
  }

  getOverduePayments(): Observable<any> {
    return this.http.get(`${this.baseUrl}/payments/overdue`);
  }

  getCompanyPayments(companyId: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/payments/company/${companyId}`);
  }

  getPaymentStats(): Observable<any> {
    return this.http.get(`${this.baseUrl}/payments/stats`);
  }

  getPaymentAging(): Observable<any> {
    return this.http.get(`${this.baseUrl}/payments/aging`);
  }

  // Connections
  createConnection(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/connections`, data);
  }

  getContactConnections(contactId: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/connections/contact/${contactId}`);
  }

  getCompanyNetwork(companyId: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/connections/network/${companyId}`);
  }

  getConnectionPath(contactA: number | string, contactB: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/connections/path/${contactA}/${contactB}`);
  }

  getLeverageSuggestions(companyId: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/connections/leverage/${companyId}`);
  }

  getKpiLeaderboard(): Observable<any> {
    return this.http.get(`${this.baseUrl}/kpi/leaderboard`);
  }

  // Geocoding
  geocodeAllCompanies(): Observable<any> {
    return this.http.post(`${this.baseUrl}/companies/geocode-all`, {});
  }

  // Bulk assignment
  bulkAssignCompanies(company_ids: number[], owner_id?: number, manager_id?: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/companies/bulk-assign`, { company_ids, owner_id, manager_id });
  }

  // Import
  importCompanies(rows: any[], duplicateAction: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/import/companies`, { rows, duplicateAction });
  }

  importContacts(rows: any[]): Observable<any> {
    return this.http.post(`${this.baseUrl}/import/contacts`, { rows });
  }

  importLeads(rows: any[]): Observable<any> {
    return this.http.post(`${this.baseUrl}/import/leads`, { rows });
  }

  getImportHistory(): Observable<any> {
    return this.http.get(`${this.baseUrl}/import/history`);
  }

  downloadImportTemplate(type: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/import/template/${type}`, { responseType: 'blob' });
  }

  // Company Locations
  getCompanyLocations(companyId: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/company-locations?company_id=${companyId}`);
  }

  getAllLocations(): Observable<any> {
    return this.http.get(`${this.baseUrl}/company-locations/all`);
  }

  getDeliveryPoints(): Observable<any> {
    return this.http.get(`${this.baseUrl}/company-locations/delivery-points`);
  }

  createCompanyLocation(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/company-locations`, data);
  }

  updateCompanyLocation(id: number | string, data: Record<string, any>): Observable<any> {
    return this.http.put(`${this.baseUrl}/company-locations/${id}`, data);
  }

  deleteCompanyLocation(id: number | string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/company-locations/${id}`);
  }

  // Quotations
  getQuotations(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/quotations`, { params: this.buildParams(params) });
  }

  getQuotation(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/quotations/${id}`);
  }

  getQuotationStats(): Observable<any> {
    return this.http.get(`${this.baseUrl}/quotations/stats`);
  }

  createQuotation(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/quotations`, data);
  }

  updateQuotation(id: number | string, data: Record<string, any>): Observable<any> {
    return this.http.put(`${this.baseUrl}/quotations/${id}`, data);
  }

  changeQuotationStatus(id: number | string, status: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/quotations/${id}/status`, { status });
  }

  deleteQuotation(id: number | string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/quotations/${id}`);
  }

  calculateQuotationOptions(id: number | string): Observable<any> {
    return this.http.post(`${this.baseUrl}/quotations/${id}/calculate-options`, {});
  }

  getQuotationOptions(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/quotations/${id}/options`);
  }

  updateQuotationOption(quotationId: number | string, optionId: number | string, data: Record<string, any>): Observable<any> {
    return this.http.put(`${this.baseUrl}/quotations/${quotationId}/options/${optionId}`, data);
  }

  sendQuotationToCustomer(id: number | string): Observable<any> {
    return this.http.post(`${this.baseUrl}/quotations/${id}/send-to-customer`, {});
  }

  getQuotationPdf(id: number | string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/quotations/${id}/pdf`, { responseType: 'blob' });
  }

  reviseQuotation(id: number | string): Observable<any> {
    return this.http.post(`${this.baseUrl}/quotations/${id}/revise`, {});
  }

  // Shipping Ops
  getShippingOps(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/shipping-ops`, { params: this.buildParams(params) });
  }

  getShippingOp(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/shipping-ops/${id}`);
  }

  createShippingOp(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/shipping-ops`, data);
  }

  updateShippingOp(id: number | string, data: Record<string, any>): Observable<any> {
    return this.http.put(`${this.baseUrl}/shipping-ops/${id}`, data);
  }

  changeShippingOpStatus(id: number | string, status: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/shipping-ops/${id}/status`, { status });
  }

  // Senders
  getSenders(): Observable<any> {
    return this.http.get(`${this.baseUrl}/senders`);
  }

  createSender(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/senders`, data);
  }

  updateSender(id: number | string, data: Record<string, any>): Observable<any> {
    return this.http.put(`${this.baseUrl}/senders/${id}`, data);
  }

  deleteSender(id: number | string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/senders/${id}`);
  }

  // Country Zones
  getCountryZones(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/country-zones`, { params: this.buildParams(params) });
  }

  createCountryZone(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/country-zones`, data);
  }

  bulkImportCountryZones(zones: any[]): Observable<any> {
    return this.http.post(`${this.baseUrl}/country-zones/bulk`, { zones });
  }

  updateCountryZone(id: number | string, data: Record<string, any>): Observable<any> {
    return this.http.put(`${this.baseUrl}/country-zones/${id}`, data);
  }

  deleteCountryZone(id: number | string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/country-zones/${id}`);
  }

  // ODA/OPA
  getOdaOpa(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/country-zones/oda-opa`, { params: this.buildParams(params) });
  }

  getOdaOpaCountries(): Observable<any> {
    return this.http.get(`${this.baseUrl}/country-zones/oda-opa/countries`);
  }

  getOdaOpaStats(): Observable<any> {
    return this.http.get(`${this.baseUrl}/country-zones/oda-opa/stats`);
  }

  checkOdaOpa(params: { country: string; city?: string; postal?: string }): Observable<any> {
    return this.http.get(`${this.baseUrl}/country-zones/oda-opa/check`, { params: this.buildParams(params) });
  }

  // Price Matrix
  getPriceMatrix(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/price-matrix`, { params: this.buildParams(params) });
  }

  calculatePrice(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/price-matrix/calculate`, data);
  }

  createPriceEntry(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/price-matrix`, data);
  }

  uploadPriceMatrix(rows: any[]): Observable<any> {
    return this.http.post(`${this.baseUrl}/price-matrix/upload`, { rows });
  }

  updatePriceEntry(id: number | string, data: Record<string, any>): Observable<any> {
    return this.http.put(`${this.baseUrl}/price-matrix/${id}`, data);
  }

  deletePriceEntry(id: number | string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/price-matrix/${id}`);
  }

  // Services
  getServices(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/services`, { params: this.buildParams(params) });
  }

  getService(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/services/${id}`);
  }

  createService(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/services`, data);
  }

  updateService(id: number | string, data: Record<string, any>): Observable<any> {
    return this.http.put(`${this.baseUrl}/services/${id}`, data);
  }

  deleteService(id: number | string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/services/${id}`);
  }

  getServicePrices(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/services/${id}/prices`);
  }

  addServicePrice(id: number | string, data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/services/${id}/prices`, data);
  }

  updateServicePrice(serviceId: number | string, priceId: number | string, data: Record<string, any>): Observable<any> {
    return this.http.put(`${this.baseUrl}/services/${serviceId}/prices/${priceId}`, data);
  }

  deleteServicePrice(serviceId: number | string, priceId: number | string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/services/${serviceId}/prices/${priceId}`);
  }

  bulkUploadServicePrices(id: number | string, rows: any[]): Observable<any> {
    return this.http.post(`${this.baseUrl}/services/${id}/prices/bulk`, { rows });
  }

  // WCA Partners
  getWcaPartners(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/wca-partners`, { params: this.buildParams(params) });
  }

  getWcaPartner(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/wca-partners/${id}`);
  }

  createWcaPartner(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/wca-partners`, data);
  }

  updateWcaPartner(id: number | string, data: Record<string, any>): Observable<any> {
    return this.http.put(`${this.baseUrl}/wca-partners/${id}`, data);
  }

  deleteWcaPartner(id: number | string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/wca-partners/${id}`);
  }

  // Route Quotes
  getRouteQuotes(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/route-quotes`, { params: this.buildParams(params) });
  }

  getRouteQuote(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/route-quotes/${id}`);
  }

  createRouteQuote(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/route-quotes`, data);
  }

  updateRouteQuote(id: number | string, data: Record<string, any>): Observable<any> {
    return this.http.put(`${this.baseUrl}/route-quotes/${id}`, data);
  }

  deleteRouteQuote(id: number | string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/route-quotes/${id}`);
  }

  addRouteLeg(quoteId: number | string, data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/route-quotes/${quoteId}/legs`, data);
  }

  updateRouteLeg(quoteId: number | string, legId: number | string, data: Record<string, any>): Observable<any> {
    return this.http.put(`${this.baseUrl}/route-quotes/${quoteId}/legs/${legId}`, data);
  }

  deleteRouteLeg(quoteId: number | string, legId: number | string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/route-quotes/${quoteId}/legs/${legId}`);
  }

  recalculateRouteQuote(id: number | string): Observable<any> {
    return this.http.post(`${this.baseUrl}/route-quotes/${id}/recalculate`, {});
  }

  getRouteQuotePdf(id: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/route-quotes/${id}/pdf`, { responseType: 'blob' });
  }

  duplicateRouteQuote(id: number | string): Observable<any> {
    return this.http.post(`${this.baseUrl}/route-quotes/${id}/duplicate`, {});
  }

  getRouteQuoteDashboard(): Observable<any> {
    return this.http.get(`${this.baseUrl}/route-quotes/stats/dashboard`);
  }

  sendQuoteEmail(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/email-settings/send-quote`, data);
  }

  // Lane History
  searchLaneHistory(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/lane-history/search`, { params: this.buildParams(params) });
  }

  recordLaneHistory(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/lane-history`, data);
  }

  getTopLanes(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/lane-history/top-lanes`, { params: this.buildParams(params) });
  }

  // Email Settings
  getEmailSettings(): Observable<any> {
    return this.http.get(`${this.baseUrl}/email-settings`);
  }

  updateEmailSettings(data: Record<string, any>): Observable<any> {
    return this.http.put(`${this.baseUrl}/email-settings`, data);
  }

  testEmailConnection(data?: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/email-settings/test`, data || {});
  }

  getEmailLog(params?: Record<string, any>): Observable<any> {
    return this.http.get(`${this.baseUrl}/email-settings/log`, { params: this.buildParams(params) });
  }

  // Quote Events
  getQuoteEvents(quoteId: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/quote-events/${quoteId}`);
  }

  recordQuoteEvent(data: Record<string, any>): Observable<any> {
    return this.http.post(`${this.baseUrl}/quote-events`, data);
  }
}
